import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { Label } from "@/components/ui/baseUi/label"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { SIDEBAR_CONTEXT_DEFAULT_WIDTH } from "@/modules/sidebar/context/sidebarContext"
import { useTrainingProgramFilterActions } from "@/modules/training/stores/useTrainingProgramFilterStore"
import { useUser } from "@/modules/users/hooks/useUser"
import ProgramEmpty from "@images/empty/program_empty.svg"
import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { ProLargeIcon } from "@images/svgIcons/proLargeIcon"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ProgramFilter, ProgramStatus, ProgramType, SharedCoachStatus } from "@modules/programs/types/programTypes"
import { getDisplayStatus, getSubscriptionCountText } from "@modules/programs/utils/programUtils"
import { useAllProgramsSelected, useProgramSelected } from "@modules/training/hooks/useTraining"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { EmptyView } from "@modules/ui/components/emptyView"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { createColumnHelper } from "@tanstack/react-table"
import { useMemo } from "react"

type TrainingProgramsFiltersProps = {
  programFilter: ProgramFilter
  allowProgramSelection: boolean
  title?: string
  tagClassName?: string
  emptyTitle?: string
  emptyDescription?: string
  emptyButtonLabel?: string
  emptyButtonIcon?: React.JSX.Element
  emptyButtonAction?: () => void
} & React.ComponentProps<"div">

const columnHelper = createColumnHelper<UserProgramItemEntity>()

export function TrainingProgramsFilters({
  programFilter,
  allowProgramSelection,
  title,
  tagClassName,
  emptyTitle,
  emptyDescription,
  emptyButtonLabel,
  emptyButtonIcon,
  emptyButtonAction,
  className,
  ...props
}: TrainingProgramsFiltersProps) {
  const user = useUser()

  const programs = usePrograms({ programFilter })
  const mySpaceNavigate = useMySpaceNavigate()
  const isAllSelected = useAllProgramsSelected(programs.data.content)
  const { width } = useWindowSize()
  const { addSelectedProgram, removeSelectedProgram } = useTrainingProgramFilterActions()

  const onSelectedProgram = (program: UserProgramItemEntity) => {
    if (program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO) {
      mySpaceNavigate(Path.MySpace.ProgramDetails(program.id))
    } else if (program.type === ProgramType.ONOFF) {
      mySpaceNavigate(Path.MySpace.ProgramOnOffDetails(program.id))
    }
  }

  // Check All Programs
  const selectAll = () => {
    programs.data.content.forEach((program) => {
      addSelectedProgram(program)
    })
  }

  // Uncheck All Programs
  const deselectAll = () => {
    programs.data.content.forEach((program) => {
      removeSelectedProgram(program)
    })
  }

  const columns = [
    columnHelper.accessor("id", {
      id: "checkbox",
      header: "",
      cell: ({ row: { original: program } }) => <CheckboxTemplate program={program} />,
      meta: {
        className: allowProgramSelection ? "px-2! py-3" : "",
        style: { height: 54 },
        onClick: (e) => {
          e.stopPropagation()
        },
        hidden: !allowProgramSelection,
      },
    }),
    columnHelper.accessor("name", {
      header: () => (
        <Text fontStyle="LM" className="text-textDefault-caption">
          {word("global.name")}
        </Text>
      ),
      cell: ({ row: { original: program } }) => (
        <div className="flex flex-col items-start overflow-hidden">
          <Text
            fontStyle="LL"
            className="w-full cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap"
            title={program.name}
          >
            {program.name}
          </Text>
          {"status" in program && program.status === ProgramStatus.DRAFT && (
            <Badge variant="neutralIntense" size="md">
              {getDisplayStatus(program.status)}
            </Badge>
          )}
        </div>
      ),
      meta: {
        className: "px-2! py-0 overflow-hidden overflow-ellipsis cursor-pointer",
        headerClassName: "px-2!",
        style: { maxWidth: 170, height: 54 },
      },
    }),
    columnHelper.accessor("owner", {
      header: () => (
        <Text fontStyle="LM" className="text-textDefault-caption">
          {word("programs.global.founder")}
        </Text>
      ),
      cell: (info) => (
        <Text
          title={`${info.getValue()?.firstname} ${info.getValue()?.lastname}`}
          className="block w-full overflow-hidden font-normal overflow-ellipsis whitespace-nowrap"
          fontStyle="LS"
        >
          {`${info.getValue()?.firstname} ${info.getValue()?.lastname}`}
        </Text>
      ),
      meta: {
        className: "px-2! py-0 cursor-pointer",
        headerClassName: "px-2!",
        style: { maxWidth: 130, height: 54 },
      },
    }),
    columnHelper.accessor("subscriptionCount", {
      id: "subscriptionCount",
      header: () => (
        <Text fontStyle="LM" className="text-textDefault-caption">
          {word("program.label.subscribers")}
        </Text>
      ),
      cell: ({ row: { original: program } }) => (
        <Text fontStyle="BS">
          {getSubscriptionCountText(
            program.subscriptionCount,
            program.owner?.id === user?.id,
            program.sharedCoachStatus === SharedCoachStatus.ACCEPTED,
            true,
            program.type,
          ).toLowerCase()}
        </Text>
      ),
      meta: {
        className: "px-2! py-0 cursor-pointer",
        headerClassName: "px-2!",
        style: { maxWidth: 100, height: 54 },
      },
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: () => (
        <Text fontStyle="LM" className="text-textDefault-caption">
          {word("program.type")}
        </Text>
      ),
      cell: ({ row: { original: program } }) => (
        <div className="flex w-full flex-row items-center justify-center">
          {program.type === ProgramType.VIRTUAL ? (
            <InfinityIcon color="currentColor" width={24} height={24} className="shrink-0 text-neutral-900" />
          ) : program.type === ProgramType.ONOFF ? (
            <OnOffIcon color="currentColor" width={24} height={24} className="shrink-0 text-neutral-900" />
          ) : (
            <ProLargeIcon color="currentColor" width={24} height={24} className="shrink-0 text-neutral-900" />
          )}
        </div>
      ),
      meta: {
        className: "px-2! py-0 cursor-pointer",
        headerClassName: "px-2!",
        style: { maxWidth: 60, height: 54 },
      },
    }),
  ].filter((column) => {
    if (column.id === "subscriptionCount" || column.id === "_programType") return width >= SIDEBAR_CONTEXT_DEFAULT_WIDTH
    return true
  })

  if (programFilter === ProgramFilter.SHARED_COACH && programs.data.content.length === 0) {
    return null
  }

  return (
    <div className={cn("mb-3", className)} {...props}>
      <div className="mt-5 mb-4 flex justify-between">
        <Text fontStyle="TS" className={cn("rounded-4xl px-3 py-1", tagClassName)}>
          {title}
        </Text>

        {allowProgramSelection && programs.data.content.length > 0 && (
          <Label htmlFor="allPrograms">
            <Checkbox
              id="allPrograms"
              checked={isAllSelected}
              className="size-5.5"
              onCheckedChange={(checked) => {
                if (checked) {
                  selectAll()
                } else {
                  deselectAll()
                }
              }}
            />
            <Text fontStyle="TS" className="text-textDefault-body">
              {word("global.filters.selectAll")}
            </Text>
          </Label>
        )}
      </div>
      {programs.data.content.length > 0 ? (
        <HUDatatable
          data={programs.data.content}
          columns={columns}
          handleRowSelectionChange={onSelectedProgram}
          hideFooter
        />
      ) : (
        <div className="flex grow items-center justify-center">
          <EmptyView
            image={{ src: ProgramEmpty }}
            title={emptyTitle}
            description={emptyDescription}
            hidden={!emptyTitle}
            flexGrow={false}
            className="bg-surface-background p-6"
            button={
              emptyButtonAction &&
              emptyButtonLabel && (
                <Button variant="primary" size="lg" onClick={emptyButtonAction}>
                  {emptyButtonIcon}
                  {emptyButtonLabel}
                </Button>
              )
            }
            paddingType="In_table"
          />
        </div>
      )}
    </div>
  )
}

const CheckboxTemplate = ({
  program,
  className,
  ...props
}: { program: UserProgramItemEntity } & React.ComponentProps<"div">) => {
  const isSelected = useProgramSelected(program.id)
  const { addSelectedProgram, removeSelectedProgram } = useTrainingProgramFilterActions()

  const checked = useMemo(() => {
    return isSelected
  }, [isSelected])

  return (
    <div className={cn("pl-2", className)} {...props}>
      <Checkbox
        id={program.id}
        checked={checked}
        onCheckedChange={() => {
          if (isSelected) {
            removeSelectedProgram(program)
          } else {
            addSelectedProgram(program)
          }
        }}
        className="size-5.5"
      />
    </div>
  )
}
